// MAP SYSTEM (owner 2026-11-02: 7 new original arenas + the current one as map #1). The neutral, deterministic half of a
// map: what the simulation needs to know (layout parameters, hazards, time of day, night pressure, boss, unlock rule).
// Each map lives in src/maps/<id>/core.ts and exports one MapCoreDef; the renderer half (sky, ground, models) lives next to
// it in render.ts. Pure: no three, no DOM, no Math.random — everything random comes from the seeded Rng it is handed.
import type { Rng } from './util';
import type { Prop, PropKind, VariantDef } from './terrain';
import type { HazardDef } from './hazards';

/** What the player picks on the map select page. 'day' = the map's daytime look (dawn / noon / dusk, see MapTime.day),
 *  'night' = moonlit night, 'cycle' = one full day → night → day over the run. */
export type TodChoice = 'day' | 'night' | 'cycle';
export const TOD_CHOICES: readonly TodChoice[] = ['day', 'night', 'cycle'];
/** The daytime flavour a map uses for its 'day' choice. */
export type DayLook = 'dawn' | 'noon' | 'dusk';

export interface MapTime {
  /** highlighted on the select page and used when the player has not picked one for this map yet */
  default: TodChoice;
  /** which daytime look 'day' means on this map */
  day: DayLook;
}

/** Unlock rule: open from the start (no `after`), or survive `seconds` on map `after` (best time on that map). */
export interface UnlockRule { after?: string; seconds?: number }

/** Rolling hills (sum of Gaussian mounds + one long swell), flat around the spawn and blended flat before the wall. */
export interface HillSpec {
  mounds: number;
  /** chance a mound is a shallow dip instead */
  dip: number;
  /** mound radius range (m), dip radius range (m) */
  rad: [number, number]; dipRad: [number, number];
  /** mound height range (m), dip depth range (m, positive) */
  amp: [number, number]; dipAmp: [number, number];
  /** amplitude of the long low swell (m) */
  swell: number;
  /** soft height limits (m) */
  max: number; min: number;
}

/** One kind of destructible prop and how many the arena places. The five kinds are GAMEPLAY ARCHETYPES (see
 *  terrain.ts): tree = tall breakable (burns), bush = low breakable (burns), rock = small breakable, boulder = solid
 *  medium, spire = solid tall landmark. A map reskins them freely (a frozen pillar is a 'spire', a car wreck a 'boulder'). */
export interface PropSpec {
  kind: PropKind;
  /** count at the original 72 m arena radius (scaled by area to the real radius) */
  count: number;
  /** placement band: from rMin (m from the centre) to arenaR - inset */
  rMin: number; inset: number;
  /** hit radius range (m) for kinds without variants */
  size: [number, number];
  /** extra clearance (m) to other props */
  clear: number;
  /** height factor range (render scale; spires: metres before the render's own factor) */
  h: [number, number];
  /** health scales with radius / hpPer (spires 4.5, boulders 2); omit = flat */
  hpPer?: number;
  /** shape variants (trees / bushes); the renderer builds one shape per entry, in order */
  variants?: VariantDef[];
  /** 'scatter' (default) = uniform in the band; 'clumps' = small groups, half of them hugging trees (placed last, own Rng) */
  layout?: 'scatter' | 'clumps';
}

export interface TerrainSpec { hills: HillSpec | null; props: PropSpec[] }

/** Context handed to a map's hazard generator (pure: use ctx.rng only). */
export interface HazardCtx { rng: Rng; arenaR: number; /** run length (s) = the final boss time */ runLen: number; props: readonly Prop[] }

/** The map's final boss. Behaviour is the shared Tyrant archetype (charge / ring / summon); the map sets its name, its
 *  toughness and (render side) its look. */
export interface BossDef { name: string; /** health multiplier on top of the normal curve (default 1) */ hpMul?: number }

export interface MapCoreDef {
  /** folder name under src/maps/ and the save key; lowercase-kebab, never renamed once shipped */
  id: string;
  /** player-facing name + one-line tagline (select page) */
  name: string; tagline: string;
  /** position on the select page and in the unlock chain (1 = first) */
  order: number;
  /** 'wip' maps show as COMING SOON (playable only with ?mapsall); flip to 'ready' when the map is done */
  status: 'ready' | 'wip';
  time: MapTime;
  unlock: UnlockRule;
  terrain: TerrainSpec;
  /** MAP-FIRE grass / tree fires (grassland maps); false = nothing on this map catches fire (scorch marks still appear) */
  fire: boolean;
  /** MAP-FIRE mask (pure, deterministic): true where the ground can burn and carry a grass fire; omit = the whole arena
   *  (when `fire` is on). Flora always burns where it stands (fire.ts FireGrid). */
  fireMask?: (x: number, z: number) => boolean;
  /** spawn-rate multiplier at full night (default NIGHT_PRESSURE). Keep it small: night is atmosphere, not a wall. */
  nightPressure?: number;
  boss: BossDef;
  /** hazards for one run (pure function of ctx.rng); omit for none */
  hazards?: (ctx: HazardCtx) => HazardDef[];
}

/** concept art for the select page (public/maps/<id>/concept.webp; painted art simply replaces the file) */
export const conceptPath = (id: string): string => `maps/${id}/concept.webp`;

// ---------------------------------------------------------------- time of day (deterministic, sim-owned)
/** Clock: 0 = midnight, 0.25 ≈ sunrise, 0.5 = noon, 0.75 ≈ sunset (fraction of a day). */
export const DAY_PHASE: Record<DayLook, number> = { dawn: 0.27, noon: 0.5, dusk: 0.73 };
export const NIGHT_PHASE = 0;
/** Live cycle starts mid-morning, so a run opens in daylight, dusks around the middle and ends at the next dawn. */
export const CYCLE_START = 0.3;
/** Live cycle: one full day over the run, but never faster than one day per 5 minutes. */
export const MIN_CYCLE = 300;
/** default spawn-rate multiplier at full night */
export const NIGHT_PRESSURE = 1.15;

/** Time of day (0..1) at run time t (s). */
export function todClock(choice: TodChoice, day: DayLook, t: number, runLen: number): number {
  if (choice === 'day') return DAY_PHASE[day];
  if (choice === 'night') return NIGHT_PHASE;
  const c = CYCLE_START + t / Math.max(MIN_CYCLE, runLen);
  return c - Math.floor(c);
}
/** Sun height -1 (midnight) .. 1 (noon). */
export const sunHeight = (clock: number): number => -Math.cos(clock * Math.PI * 2);
/** 0 in daylight .. 1 at full night (smooth through dawn and dusk). Dawn / dusk looks stay below 0.1. */
export function nightness(clock: number): number {
  const s = sunHeight(clock), t = Math.min(1, Math.max(0, (0.15 - s) / 0.5));
  return t * t * (3 - 2 * t);
}
/** Spawn-rate multiplier for this map at this clock (1 by day). */
export const nightPressure = (def: MapCoreDef, clock: number): number => 1 + ((def.nightPressure ?? NIGHT_PRESSURE) - 1) * nightness(clock);

// ---------------------------------------------------------------- unlocks (pure; save.ts stores the inputs)
/** Map progress the unlock rules read: best survival time (s) per map id. */
export interface MapProgress { best: Record<string, number> }
export function isUnlocked(def: MapCoreDef, progress: MapProgress): boolean {
  if (!def.unlock.after) return true;
  return (progress.best[def.unlock.after] ?? 0) >= (def.unlock.seconds ?? 0);
}
